import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { ContentCopy as ContentCopyIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { PageIndices } from '@/common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { DateRangeNavigator, DateRangeView } from '@components/common/DateRangeNavigator.tsx';
import { getDateRange } from '@/utils/date-range.ts';
import { getClientActivityAiContext } from '@services/client-reports.service.ts';
import { ehiService } from '@services/ehi.service.ts';

type ContextVariant = 'activity' | 'ehi';

const VARIANTS: { value: ContextVariant; label: string }[] = [
  { value: 'activity', label: 'Стандартний контекст' },
  { value: 'ehi', label: 'Контекст AI-пояснення EHI' },
];

function loadContext(variant: ContextVariant, clientId: number, from: string, to: string): Promise<string> {
  if (variant === 'ehi') return ehiService.getPmAiContext(clientId);
  return getClientActivityAiContext(String(clientId), from, to);
}

// AI-контекст клієнта для продакт-менеджера (2026-10-14): показує РІВНО те, що реально йде в AI,
// без виклику самої моделі (без витрати квоти). Варіанти контексту - через select, один за раз
// (не кілька блоків одночасно), щоб додавання третього варіанту в майбутньому не захаращувало
// сторінку - просто новий пункт у VARIANTS + case у loadContext.
// Період (з.../по...) - лише для "Стандартний контекст" (той самий DateRangeNavigator, що й
// "Мої звіти" клієнта): за замовчуванням поточний тиждень. Контекст EHI свого періоду не має -
// рахується за вікном самого EHI (summary.window) на бекенді.
export const PmClientAiContextTab: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [variant, setVariant] = useState<ContextVariant>('activity');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateView, setDateView] = useState<DateRangeView>('week');
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { from, to } = getDateRange(currentDate, dateView);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setMarkdown('');

    loadContext(variant, clientId, from, to)
      .then(setMarkdown)
      .catch(() => setError('Не вдалося завантажити контекст.'))
      .finally(() => setLoading(false));
  }, [variant, clientId, from, to]);

  const handleCopy = () => {
    navigator.clipboard.writeText(markdown).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box data-i-doc={PageIndices.PM_CLIENT_AI_CONTEXT}>
      <PageHint iDoc={PageIndices.PM_CLIENT_AI_CONTEXT} />

      <PageCard>
        <PageToolbar
          showReset={false}
          filters={[
            {
              id: 'variant',
              type: 'select',
              label: 'Варіант контексту',
              value: variant,
              onChange: (v) => setVariant(v as ContextVariant),
              options: VARIANTS,
              minWidth: '320px',
            },
            ...(variant === 'activity' ? [{
              id: 'period',
              type: 'custom' as const,
              renderCustom: () => (
                <DateRangeNavigator
                  currentDate={currentDate}
                  onDateChange={setCurrentDate}
                  view={dateView}
                  onViewChange={setDateView}
                />
              ),
            }] : []),
          ]}
          extraActions={
            <Button
              startIcon={copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              onClick={handleCopy}
              disabled={!markdown || loading}
              color={copied ? 'success' : 'inherit'}
            >
              {copied ? 'Скопійовано' : 'Копіювати'}
            </Button>
          }
        />

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: 'text.primary',
              maxHeight: 560,
              overflowY: 'auto',
            }}
          >
            {markdown}
          </Box>
        )}
      </PageCard>
    </Box>
  );
};
